/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import {
    ImageWithFallback,
    Heading,
    Text,
    Button,
    Icon,
    ExternalLinkButton,
    HtmlParser,
    DividerNew,
} from 'sarsaparilla';

export function Infographic({
    facilityId,
    showInfographic,
    data: {
        mainTitle,
        mainDescription,
        mainLink,
        mainLinkLabel,
        hideSecondCardSecondStep,
        secondCardFirstStepTitle,
        secondCardFirstStepDescription,
        secondCardSecondStepTitle,
        secondCardSecondStepDescription,
        thirdCardTitle,
        thirdCardDescription,
        buyNowButton: {
            link: buyNowButtonLink,
            clickTagCategory: buyNowButtonClickTagCategory,
            clickTagAction: buyNowButtonClickTagAction,
            clickTagLabel: buyNowButtonClickTagLabel,
        },
    },
}) {
    const secondaryContainerClassName = hideSecondCardSecondStep
        ? 'infographic--first-step-with-one-card'
        : '';

    const handleBuyNowClick = () => {
        window.location.href = buyNowButtonLink;
    };

    if (!showInfographic || !facilityId) {
        return null;
    }

    return (
        <div className="infographic">
            <div className="infographic__img-background" />
            <div className="infographic__content">
                <div className="infographic__card infographic--card-primary">
                    <Heading
                        className="infographic__title"
                        headingLevel={2}
                        appearance="h3"
                    >
                        {mainTitle}
                    </Heading>
                    <Text className="infographic__description" size="lg">
                        {mainDescription}
                    </Text>
                    {!!mainLink && (
                        <ExternalLinkButton
                            className="infographic__button"
                            appearance="tertiary"
                            url={mainLink}
                            hasExternalIcon={false}
                            iconAfterElement={<Icon iconName="launch" />}
                        >
                            {mainLinkLabel}
                        </ExternalLinkButton>
                    )}
                </div>
                <div className="infographic__cards-container">
                    <div
                        className={`infographic__card infographic--card-secondary infographic--card-with-number infographic--card-first-step ${secondaryContainerClassName}`}
                    >
                        <span className="infographic__number-container">
                            <Text
                                className="infographic__title"
                                size="lg"
                                fontWeight="bold"
                                color="white"
                                aria-label="Step 1"
                            >
                                1
                            </Text>
                        </span>
                        <div className="infographic__card-info">
                            <div className="infographic__card-info-img-container">
                                <div className="infographic__card-info-img">
                                    <ImageWithFallback
                                        imagePath={`${process.env.CDN}/img/infographic-qr`}
                                        fallbackExtension="png"
                                        alt=""
                                    />
                                </div>
                            </div>
                            <div className="infographic__card-info-item">
                                <Heading
                                    className="infographic__subtitle"
                                    headingLevel={3}
                                    appearance="h5"
                                >
                                    {secondCardFirstStepTitle}
                                </Heading>
                                <Text className="infographic__description" size="sm">
                                    {secondCardFirstStepDescription}
                                </Text>
                            </div>
                        </div>
                        {!hideSecondCardSecondStep && (
                            <>
                                <div className="infographic__card-info-divider-container">
                                    <Text
                                        className="infographic__divider-text"
                                        size="lg"
                                        fontWeight="bold"
                                        color="white"
                                    >
                                        OR
                                    </Text>
                                    <DividerNew
                                        isDashed
                                        className="infographic__divider"
                                    />
                                </div>
                                <div className="infographic__card-info">
                                    <div className="infographic__card-info-img-container">
                                        <div className="infographic__card-info-img">
                                            <ImageWithFallback
                                                imagePath={`${process.env.CDN}/img/infographic-tent`}
                                                fallbackExtension="png"
                                                alt=""
                                            />
                                        </div>
                                    </div>
                                    <div className="infographic__card-info-item">
                                        <Heading
                                            className="infographic__subtitle"
                                            headingLevel={3}
                                            appearance="h5"
                                        >
                                            {secondCardSecondStepTitle}
                                        </Heading>
                                        <Text
                                            className="infographic__description"
                                            size="sm"
                                        >
                                            {secondCardSecondStepDescription}
                                        </Text>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                    <Heading
                        className="infographic__plus-separator"
                        headingLevel={3}
                        appearance="h1"
                        aria-hidden="true"
                    >
                        +
                    </Heading>
                    <div className="infographic__card infographic--card-secondary infographic--card-with-number infographic--card-second-step">
                        <span className="infographic__number-container">
                            <Text
                                className="infographic__title"
                                size="lg"
                                fontWeight="bold"
                                color="white"
                                aria-label="Step 2"
                            >
                                2
                            </Text>
                        </span>
                        <div className="infographic__card-info">
                            <div className="infographic__card-info-wrapper">
                                <div className="infographic__card-info-img-container">
                                    <div className="infographic__card-info-img">
                                        <ImageWithFallback
                                            imagePath={`${process.env.CDN}/img/infographic-pass`}
                                            fallbackExtension="png"
                                            alt=""
                                        />
                                    </div>
                                </div>
                                <div className="infographic__card-info-item">
                                    <Heading
                                        className="infographic__subtitle"
                                        headingLevel={3}
                                        appearance="h5"
                                    >
                                        {thirdCardTitle}
                                    </Heading>
                                    <Text className="infographic__description" size="sm">
                                        <HtmlParser html={thirdCardDescription} />
                                    </Text>
                                </div>
                            </div>
                            <Button
                                className="infographic__button"
                                appearance="tertiary"
                                clickTagCategory={buyNowButtonClickTagCategory}
                                clickTagAction={buyNowButtonClickTagAction}
                                clickTagLabel={buyNowButtonClickTagLabel}
                                onClick={handleBuyNowClick}
                            >
                                Buy Now
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

Infographic.propTypes = {
    facilityId: PropTypes.string.isRequired,
    showInfographic: PropTypes.bool,
    data: PropTypes.shape({
        mainTitle: PropTypes.string,
        mainDescription: PropTypes.string,
        mainLink: PropTypes.string,
        mainLinkLabel: PropTypes.string,
        hideSecondCardSecondStep: PropTypes.bool,
        secondCardFirstStepTitle: PropTypes.string,
        secondCardFirstStepDescription: PropTypes.string,
        secondCardSecondStepTitle: PropTypes.string,
        secondCardSecondStepDescription: PropTypes.string,
        thirdCardTitle: PropTypes.string,
        thirdCardDescription: PropTypes.string,
        buyNowButton: PropTypes.shape({
            link: PropTypes.string.isRequired,
            clickTagCategory: PropTypes.string.isRequired,
            clickTagAction: PropTypes.string.isRequired,
            clickTagLabel: PropTypes.string.isRequired,
        }).isRequired,
    }),
};

Infographic.defaultProps = {
    showInfographic: false,
    data: {
        mainTitle: '',
        mainDescription: '',
        mainLink: '',
        mainLinkLabel: '',
        hideSecondCardSecondStep: false,
        secondCardFirstStepTitle: '',
        secondCardFirstStepDescription: '',
        secondCardSecondStepTitle: '',
        secondCardSecondStepDescription: '',
        thirdCardTitle: '',
        thirdCardDescription: '',
    },
};
